import CallVolumeDistributionWidget from "@/components/widgets/call-overview/CallVolumeDistributionWidget";
import { CallQueueType } from "@/constants/queue";
import { SectionType } from "@/constants/section";
import { WIDGET_IDS } from "@/constants/widget";
import { useAppData, UserRole } from "@/contexts/AppDataContext";
import { useDashboardData } from "@/contexts/DashboardContext";
import { CallDirectionTypes } from "@/enums/call-direction";
import { CallsOverviewTableColumns } from "@/enums/call-overvew-column";
import { useWidgetsData } from "@/hooks/api/useWidgetsData";
import { useFilterCache } from "@/hooks/useFilterCache";
import { fetchSlaRankingPaged, fetchStatusOverTimePaged } from "@/lib/axios/api/widgets.api";
import { PaginatedResponse } from "@/lib/axios/types";
import { SlaRankingRow, StatusOverTimeRow } from "@/lib/axios/types/widgets.type";
import { DateRangeChange, FilterChangeEvent } from "@/types";
import { getAgentIds } from "@/utils/agentUtils";
import { calculateDateRange, DATE_RANGE_KEYS, getRecalculatedDatesIfNeeded } from "@/utils/dateRangeUtils";
import React from "react";
import FilterBar from "../../common/FilterBar";
import {
  AverageCallsPerAgentWidget,
  AverageHandlingTimeWidget,
  AverageSpeedOfAnswerWidget,
  AverageWaitingTimeWidget,
} from "../../widgets/agent-performance";
import {
  CallsDetailOverviewWidget,
  InboundDirectCallsDistributionWidget,
  InternalCallsWidget,
  OutboundCallStatusWidget,
  OutboundCallsWidget,
  TotalInboundDirectCallsWidget,
  TotalTransferredCallsWidget
} from "../../widgets/call-overview";

const widgetIds = [
  WIDGET_IDS.TOTAL_OUTBOUND_CALLS,         // 2  - OutboundCallsWidget
  WIDGET_IDS.TOTAL_INTERNAL_CALLS,         // 7  - InternalCallsWidget
  WIDGET_IDS.TOTAL_TRANSFERRED_CALLS,      // 41 - TotalTransferredCallsWidget (all transfers)
  WIDGET_IDS.AVERAGE_WAITING_TIME_TREND_INBOUND_DIRECT,         // 37 - AverageWaitingTimeWidget
  WIDGET_IDS.AVERAGE_HANDLING_TIME_TREND_INBOUND_DIRECT,        // 38 - AverageHandlingTimeWidget
  WIDGET_IDS.AVERAGE_SPEED_OF_ANSWER_INBOUND_DIRECT,            // 39 - AverageSpeedOfAnswerWidget
  WIDGET_IDS.AVERAGE_CALLS_PER_AGENT,      // 18 - AverageCallsPerAgentWidget
  WIDGET_IDS.CALL_VOLUME,                  // 19 - CallVolumeWidget
  WIDGET_IDS.INBOUND_STATUS_DISTRIBUTION,  // 20 - InboundCallStatusWidget (for inbound direct calls distribution)
  WIDGET_IDS.OUTBOUND_STATUS_DISTRIBUTION, // 21 - OutboundCallStatusWidget
  WIDGET_IDS.CALL_VOLUME_DISTRIBUTION,     // 34 - CallVolumeWidget
  WIDGET_IDS.INBOUND_DIRECT_STATUS_DISTRIBUTION, // 35 - Inbound Direct Call Status Distribution
  WIDGET_IDS.TOTAL_INBOUND_DIRECT_CALLS,   // 42  - TotalInboundDirectCallsWidget (Total Inbound Direct Calls)
];

const PAGE_LIMIT = 10;

const InstantConnectInsightSection: React.FC<{ isGeneralMode: boolean, filterClass?: string, isKeyMetrics?: boolean }> = ({ isGeneralMode, filterClass, isKeyMetrics }) => {
  // State for date range filter
  const defaultDateRangeKey = DATE_RANGE_KEYS.TODAY;
  const { current } = useDashboardData();
  const { filters, saveFilters } = useFilterCache("agent-performance", current?.id);
  const [dateRangeKey, setDateRangeKey] = React.useState<string>(filters?.dateRangeKey ?? defaultDateRangeKey);
  const [dateRange, setDateRange] = React.useState<DateRangeChange>({
    key: filters?.dateRangeKey ?? defaultDateRangeKey,
    startDate: filters?.startDate ?? null,
    endDate: filters?.endDate ?? null,
    previousStartDate: filters?.previousStartDate ?? null,
    previousEndDate: filters?.previousEndDate ?? null,
  });
  const { directAgentGroupIds, uniqueAgents, userRole } = useAppData();

  // Get all agents that belong to DirectAgentGroup
  const availableAgents = React.useMemo(() => {
    return uniqueAgents.filter(agent => agent.queueType === CallQueueType.DIRECT_AGENT_GROUP);
  }, [uniqueAgents]);

  const agentIds = React.useMemo(() => getAgentIds(uniqueAgents), [uniqueAgents]);

  const getGroupIds = (selectedQueueType: CallQueueType, queues: string[]) => {
    if (userRole === UserRole.ADMIN && queues.length === 0) {
      return null;
    }

    if (selectedQueueType === CallQueueType.CALL_QUEUE && directAgentGroupIds.length === 0) {
      return null;
    }

    return selectedQueueType !== CallQueueType.AGENT
      ? directAgentGroupIds.filter(id => queues.includes(id.toString()) || queues.length === 0)
      : null;
  };

  const getAgentMicrosoftIds = (selectedQueueType: CallQueueType, agents: string[]) => {
    if (userRole === UserRole.ADMIN && agents.length === 0) {
      return null;
    }

    if (selectedQueueType === CallQueueType.CALL_QUEUE && directAgentGroupIds.length > 0) {
      return null;
    }

    return selectedQueueType === CallQueueType.DIRECT_AGENT_GROUP ? null : (agents?.length ? agents : agentIds);
  };

  const [dateParams, setDateParams] = React.useState(() => {
    if (filters) {
      const { startDate, endDate, previousStartDate, previousEndDate } = getRecalculatedDatesIfNeeded(
        filters.dateRangeKey,
        filters
      );
      
      setDateRange({
        key: filters?.dateRangeKey,
        startDate,
        endDate,
        previousStartDate,
        previousEndDate,
      });
      return {
        startDate,
        endDate,
        previousStartDate,
        previousEndDate,
        widgetIds,
        queueMicrosoftIds: null,
        agentMicrosoftIds: getAgentMicrosoftIds(filters.selectedQueueType, filters.agents || []),
        widgetPagination: {
          [WIDGET_IDS.SLA_RANKING]: { page: 1, limit: PAGE_LIMIT },
          [WIDGET_IDS.STATUS_OVER_TIME]: { page: 1, limit: PAGE_LIMIT }
        },
        groupIds: getGroupIds(filters.selectedQueueType, filters.queues),
        timeStamp: new Date().toISOString()
      };
    }
    const dateRange = calculateDateRange(defaultDateRangeKey);
    return {
      startDate: dateRange.from.toISOString(),
      endDate: dateRange.to.toISOString(),
      previousStartDate: dateRange.previousFrom.toISOString(),
      previousEndDate: dateRange.previousTo.toISOString(),
      widgetIds,
      queueMicrosoftIds: null,
      agentMicrosoftIds: userRole === UserRole.ADMIN ? null : null,
      widgetPagination: {
        [WIDGET_IDS.SLA_RANKING]: { page: 1, limit: PAGE_LIMIT },
        [WIDGET_IDS.STATUS_OVER_TIME]: { page: 1, limit: PAGE_LIMIT }
      },
      groupIds: userRole === UserRole.ADMIN ? null : (directAgentGroupIds.length > 0 ? directAgentGroupIds : null),
      timeStamp: new Date().toISOString()
    };
  });

  const [statusPage, setStatusPage] = React.useState<number>(1);
  const [slaPage, setSlaPage] = React.useState<number>(1);

  // Local widget-only pagination state (for isolated reloads)
  const [slaPaged, setSlaPaged] = React.useState<PaginatedResponse<SlaRankingRow> | null>(null);
  const [statusPaged, setStatusPaged] = React.useState<PaginatedResponse<StatusOverTimeRow> | null>(null);
  const [slaLoading, setSlaLoading] = React.useState<boolean>(false);
  const [statusLoading, setStatusLoading] = React.useState<boolean>(false);

  // Refresh data when tab becomes active
  React.useEffect(() => {
    if (isGeneralMode) {
      setDateParams(prev => ({
        ...prev,
        timeStamp: new Date().toISOString()
      }));
    }
  }, [isGeneralMode]);

  const handleFilterChange = (event: FilterChangeEvent) => {
    setStatusPage(1);
    setSlaPage(1);

    setDateRangeKey(event.dateRange.key);
    
    // Recalculate dates for dynamic date ranges to ensure freshness
    const recalculatedDates = getRecalculatedDatesIfNeeded(event.dateRange.key, {
      startDate: event.dateRange.startDate.toISOString(),
      endDate: event.dateRange.endDate.toISOString(),
      previousStartDate: event.dateRange.previousStartDate.toISOString(),
      previousEndDate: event.dateRange.previousEndDate.toISOString(),
    });
    
    setDateRange({
      key: event.dateRange.key,
      startDate: recalculatedDates.startDate,
      endDate: recalculatedDates.endDate,
      previousStartDate: recalculatedDates.previousStartDate,
      previousEndDate: recalculatedDates.previousEndDate,
    });
    setDateParams(prev => ({
      ...prev,
      startDate: recalculatedDates.startDate,
      endDate: recalculatedDates.endDate,
      previousStartDate: recalculatedDates.previousStartDate,
      previousEndDate: recalculatedDates.previousEndDate,
      widgetPagination: {
        [WIDGET_IDS.SLA_RANKING]: { page: 1, limit: PAGE_LIMIT },
        [WIDGET_IDS.STATUS_OVER_TIME]: { page: 1, limit: PAGE_LIMIT }
      },
      // queueMicrosoftIds: event.selectedQueueType === CallQueueType.CALL_QUEUE ? getArrayOrAll(event.queues) : null,
      queueMicrosoftIds: null,
      agentMicrosoftIds: getAgentMicrosoftIds(event.selectedQueueType, event.agents || []),
      groupIds: getGroupIds(event.selectedQueueType, event.queues),
      timeStamp: new Date().toISOString()
    }));

    // persist
    saveFilters({
      dateRangeKey: event.dateRange.key,
      startDate: event.dateRange.startDate.toISOString(),
      endDate: event.dateRange.endDate.toISOString(),
      previousStartDate: event.dateRange.previousStartDate.toISOString(),
      previousEndDate: event.dateRange.previousEndDate.toISOString(),
      queues: event.queues ?? [],
      agents: event.agents ?? [],
      selectedQueueType: event.selectedQueueType,
    });
  };

  // Call widgets API when dateParams changes
  const { data, loading, error } = useWidgetsData(dateParams);

  // Reset local paged caches when filters change
  React.useEffect(() => {
    setSlaPaged(null);
    setStatusPaged(null);
  }, [
    dateParams.startDate,
    dateParams.endDate,
    dateParams.previousStartDate,
    dateParams.previousEndDate,
    JSON.stringify(dateParams.queueMicrosoftIds),
    JSON.stringify(dateParams.agentMicrosoftIds),
    JSON.stringify(dateParams.groupIds)
  ]);

  // Isolated fetch for SLA Ranking when page changes (avoid refetching all widgets)
  React.useEffect(() => {
    if (!dateParams?.startDate || !dateParams?.endDate) return;
    // For page 1 use initial widgets payload; clear local cache
    if (slaPage === 1) {
      setSlaPaged(null);
      return;
    }
    setSlaLoading(true);
    fetchSlaRankingPaged({
      startDate: dateParams.startDate,
      endDate: dateParams.endDate,
      previousStartDate: dateParams.previousStartDate,
      previousEndDate: dateParams.previousEndDate,
      queueMicrosoftIds: dateParams.queueMicrosoftIds ?? undefined,
      agentMicrosoftIds: dateParams.agentMicrosoftIds ?? undefined,
      groupIds: dateParams.groupIds ?? undefined,
      page: slaPage,
      limit: PAGE_LIMIT
    })
      .then(setSlaPaged)
      .finally(() => setSlaLoading(false));
  }, [slaPage, dateParams]);

  // Isolated fetch for Status Over Time when page changes (avoid refetching all widgets)
  React.useEffect(() => {
    if (!dateParams?.startDate || !dateParams?.endDate) return;
    if (statusPage === 1) {
      setStatusPaged(null);
      return;
    }
    setStatusLoading(true);
    fetchStatusOverTimePaged({
      startDate: dateParams.startDate,
      endDate: dateParams.endDate,
      previousStartDate: dateParams.previousStartDate,
      previousEndDate: dateParams.previousEndDate,
      queueMicrosoftIds: dateParams.queueMicrosoftIds ?? undefined,
      agentMicrosoftIds: dateParams.agentMicrosoftIds ?? undefined,
      groupIds: dateParams.groupIds ?? undefined,
      page: statusPage,
      limit: PAGE_LIMIT
    })
      .then(setStatusPaged)
      .finally(() => setStatusLoading(false));
  }, [statusPage, dateParams]);


  const [callsDetailFilters, setCallsDetailFilters] = React.useState<{ [key: string]: string[] }>({});
  const [callMicrosoftIds, setCallMicrosoftIds] = React.useState<string[] | undefined>(undefined);
  const callsDetailWidgetRef = React.useRef<HTMLDivElement>(null);

  const handleWidgetClick = (filters: { [key: string]: string[] }, callMicrosoftIds?: string[]) => {
    setCallsDetailFilters(filters);
    // Use callMicrosoftIds from parameter if provided, otherwise extract from filters for backward compatibility
    if (callMicrosoftIds && callMicrosoftIds.length > 0) {
      setCallMicrosoftIds(callMicrosoftIds);
    } else if (filters?.callMicrosoftIds?.length > 0) {
      setCallMicrosoftIds(filters.callMicrosoftIds);
    } else {
      setCallMicrosoftIds(undefined);
    }
    setTimeout(() => {
      if (callsDetailWidgetRef.current) {
        callsDetailWidgetRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const renderOverviewSection = () => {
    return <section>
      <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 gap-4">
        <OutboundCallsWidget data={{ value: data?.callOverviewData?.totalOutboundCalls?.totalOutboundCalls as number, comparisonPercentage: data?.callOverviewData?.totalOutboundCalls?.comparisonPercentage as number }} loading={loading} error={error} onClick={handleWidgetClick} />
        <TotalInboundDirectCallsWidget data={{ value: data?.callOverviewData?.totalInboundDirectCalls?.totalInboundDirectCalls as number, comparisonPercentage: data?.callOverviewData?.totalInboundDirectCalls?.comparisonPercentage as number }} loading={loading} error={error} onClick={handleWidgetClick} />
        <InternalCallsWidget data={{ value: data?.callOverviewData?.totalInternalCalls?.totalInternalCalls as number, comparisonPercentage: data?.callOverviewData?.totalInternalCalls?.comparisonPercentage as number }} loading={loading} error={error} onClick={handleWidgetClick} />
        <TotalTransferredCallsWidget data={{ value: data?.callOverviewData?.totalTransferredCalls?.totalTransferredCalls ?? 0, comparisonPercentage: data?.callOverviewData?.totalTransferredCalls?.comparisonPercentage as number, callMicrosoftIds: data?.callOverviewData?.totalTransferredCalls?.callMicrosoftIds }} loading={loading} error={error} onClick={handleWidgetClick} />
        <AverageWaitingTimeWidget data={{ id: 'average-waiting-time-widget', value: data?.averageWaitingTimeInboundDirect ?? 0, comparisonPercentage: data?.comparisonPercentageAWTInboundDirect != null ? data.comparisonPercentageAWTInboundDirect : null }} loading={loading} error={error} />
        <AverageHandlingTimeWidget data={{ id: 'average-handling-time-widget', value: data?.averageHandlingTimeInboundDirect ?? 0, comparisonPercentage: data?.comparisonPercentageAHTInboundDirect != null ? data.comparisonPercentageAHTInboundDirect : null }} loading={loading} error={error} />
        <AverageSpeedOfAnswerWidget data={{ id: 'average-speed-of-answer-widget', value: data?.averageSpeedOfAnswerInboundDirect ?? 0, comparisonPercentage: data?.comparisonPercentageASOInboundDirect != null ? data.comparisonPercentageASOInboundDirect : null }} loading={loading} error={error} />
        <AverageCallsPerAgentWidget data={{ id: 'average-calls-per-agent-widget', value: data?.averageCallsPerAgent ?? 0, comparisonPercentage: data?.comparisonPercentageACP != null ? data.comparisonPercentageACP : null }} loading={loading} error={error} />
      </div>
    </section>
  }

  const renderDetailsSection = () => {
    return <section>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <OutboundCallStatusWidget
          data={{
            answeredCall: (data?.callOverviewData?.outboundStatusDistribution as { answeredCall: number; missedCall: number })?.answeredCall ?? 0,
            missedCall: (data?.callOverviewData?.outboundStatusDistribution as { answeredCall: number; missedCall: number })?.missedCall ?? 0
          }}
          loading={loading}
          error={error}
          onClick={handleWidgetClick}
        />
        <InboundDirectCallsDistributionWidget
          data={{
            answeredCall: (data?.callOverviewData?.['inboundDirectStatusDistribution'] as { answeredCall: number; missedCall: number })?.answeredCall ?? 0,
            missedCall: (data?.callOverviewData?.['inboundDirectStatusDistribution'] as { answeredCall: number; missedCall: number })?.missedCall ?? 0
          }}
          loading={loading}
          error={error}
          onClick={handleWidgetClick}
        />
      </div>
      <div className="mt-4">
        <CallVolumeDistributionWidget
          data={{
            callOverviewData: {
              callVolumeDistribution: (data?.callOverviewData?.callVolumeDistribution as Array<{ time: Date; inboundDirectCall: number; internalCall: number; outboundCall: number; }>) ?? []
            }
          }}
          dateRangeType={dateRangeKey}
          dateRange={{ from: new Date(dateRange.startDate || ''), to: new Date(dateRange.endDate || '') }}
          loading={loading}
          error={error}
        />
      </div>
      <div className="mt-4" ref={callsDetailWidgetRef}>
        <CallsDetailOverviewWidget
          callDirectionTypes={[CallDirectionTypes.INBOUND_DIRECT, CallDirectionTypes.OUTBOUND, CallDirectionTypes.INTERNAL]}
          dateParams={dateParams}
          hiddenColumns={[CallsOverviewTableColumns.CALL_QUEUE, CallsOverviewTableColumns.RECIPIENT]}
          initialFilters={callsDetailFilters}
          callMicrosoftIds={callMicrosoftIds}
        />
      </div>
    </section>
  }

  return (
    <div className="w-full">
      {/* Date Range & Filters */}
      <div className={filterClass}>
        <FilterBar
          onFilterChange={handleFilterChange}
          initialDateRangeKey={dateRangeKey}
          initialSelectedQueues={filters?.queues}
          initialSelectedAgents={filters?.agents}
          initialSelectedQueueType={filters?.selectedQueueType}
          initialDateRange={dateRange}
          sectionType={SectionType.INSTANT_CONNECT_INSIGHT}
          availableAgents={availableAgents}
        />
      </div>
      {renderOverviewSection()}
      {renderDetailsSection()}
    </div>
  );
};

export default InstantConnectInsightSection;
